import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Loader2, Compass } from "lucide-react";
import PublicShell from "@/components/PublicShell";
import { usePageMeta } from "@/lib/usePageMeta";
import api from "@/lib/api";

export default function HolidayDestinationsPage() {
  usePageMeta("Holiday Destinations · Suffle Online", "Explore curated India & international holiday destinations by theme — beach, honeymoon, adventure, hill stations, heritage and more. Find matching holiday packages.");
  const [data, setData] = useState({ destinations: [], themes: [] });
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get("/travel/holiday-destinations", { params: theme ? { theme } : {} })
      .then((r) => setData({ destinations: r.data.destinations || [], themes: r.data.themes || data.themes }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [theme]); // eslint-disable-line

  return (
    <PublicShell>
      <div className="mx-auto max-w-6xl px-4 py-8" data-testid="holiday-destinations-page">
        <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-fuchsia-50 px-3 py-1 text-xs font-semibold text-fuchsia-700"><Compass className="h-3.5 w-3.5" /> Destination Guides</div>
        <h1 className="text-3xl font-bold text-slate-900">Holiday destinations</h1>
        <p className="mt-1 text-sm text-slate-500">Pick a destination to see holiday packages our verified agents offer — or browse by theme.</p>

        <div className="mt-5 flex flex-wrap gap-1.5" data-testid="destinations-theme-filter">
          <button onClick={() => setTheme("")} data-testid="dest-theme-all" className={`rounded-full px-3 py-1 text-xs font-semibold ${!theme ? "bg-fuchsia-600 text-white" : "border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>All</button>
          {(data.themes || []).map((t) => (
            <button key={t} onClick={() => setTheme(theme === t ? "" : t)} data-testid={`dest-theme-${t}`} className={`rounded-full px-3 py-1 text-xs font-semibold ${theme === t ? "bg-fuchsia-600 text-white" : "border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{t}</button>
          ))}
        </div>

        {loading ? <div className="flex h-48 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="destinations-grid">
            {data.destinations.map((d) => (
              <Link key={d.slug} to={`/holidays/${d.slug}`} data-testid={`destination-card-${d.slug}`} className="group overflow-hidden rounded-2xl border border-slate-200 bg-white transition hover:-translate-y-0.5 hover:shadow-md">
                <div className="h-28 w-full overflow-hidden bg-slate-100">{d.image ? <img src={d.image} alt={d.name} loading="lazy" className="h-full w-full object-cover transition duration-300 group-hover:scale-105" /> : <div className="flex h-full items-center justify-center bg-gradient-to-br from-fuchsia-500 via-violet-500 to-sky-500 text-white"><MapPin className="h-5 w-5" /></div>}</div>
                <div className="p-3">
                  <p className="text-sm font-bold text-slate-800">{d.name}</p>
                  <p className="text-[11px] text-slate-400">{d.country}</p>
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {(d.themes || []).slice(0, 3).map((t) => <span key={t} className="rounded-full bg-fuchsia-50 px-2 py-0.5 text-[10px] font-semibold text-fuchsia-600">{t}</span>)}
                  </div>
                </div>
              </Link>
            ))}
            {data.destinations.length === 0 && <p className="col-span-full rounded-2xl border border-slate-100 p-10 text-center text-sm text-slate-400">No destinations for this theme.</p>}
          </div>
        )}
      </div>
    </PublicShell>
  );
}
